import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Alert,
  Snackbar,
  Tooltip,
  Fab,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Water as WaterIcon,
  LocalShipping as LocalShippingIcon,
  Medication as MedicationIcon,
  Sell as SellIcon,
  AccountBalance as AccountBalanceIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Agriculture as AgricultureIcon
} from '@mui/icons-material';
import { requestsAPI, pondsAPI, farmsAPI } from 'services/api';

const RequestManagement = ({ filterTypes, pageTitle }) => {
  const [requests, setRequests] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [allPonds, setAllPonds] = useState([]); // Store all ponds for filtering
  const [filteredPonds, setFilteredPonds] = useState([]); // Filtered ponds based on request type
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Form data
  const [formData, setFormData] = useState({
    pond_id: '',
    request_type: '',
    title: '',
    description: '',
    quantity: '',
    unit: '',
    estimated_cost: '',
    doc_period: '',
    expected_harvest_date: '',
    loan_amount: '',
    loan_type: '',
    repayment_period: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});

  const allRequestTypes = [
    { value: 'seed', label: 'Seed Request', icon: <WaterIcon />, color: '#4caf50' },
    { value: 'feed', label: 'Feed Request', icon: <LocalShippingIcon />, color: '#ff9800' },
    { value: 'medicine', label: 'Medicine Request', icon: <MedicationIcon />, color: '#f44336' },
    { value: 'crop_sale', label: 'Crop Sale Request', icon: <SellIcon />, color: '#2196f3' },
    { value: 'loan', label: 'Loan Request', icon: <AccountBalanceIcon />, color: '#9c27b0' }
  ];

  // Apply optional type filtering (used by Credit & Escrow, Harvest & Settlement pages)
  const requestTypes = filterTypes
    ? allRequestTypes.filter((rt) => filterTypes.includes(rt.value))
    : allRequestTypes;

  const loanTypes = [
    { value: 'crop_investment', label: 'Crop Investment' },
    { value: 'input_purchase', label: 'Input Purchase' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'operational', label: 'Operational' }
  ];

  const units = [
    { value: 'pieces', label: 'Pieces' },
    { value: 'kg', label: 'Kilograms' },
    { value: 'liters', label: 'Liters' },
    { value: 'bags', label: 'Bags' },
    { value: 'boxes', label: 'Boxes' }
  ];

  const steps = ['Request Type', 'Pond Selection', 'Request Details', 'Review & Submit'];

  useEffect(() => {
    loadData();
  }, []);

  // Filter ponds based on request type and pond status
  useEffect(() => {
    if (allPonds.length > 0 && formData.request_type) {
      filterPondsByRequestType();
    }
  }, [allPonds, formData.request_type]);

  const filterPondsByRequestType = () => {
    let filtered = [];
    const currentDate = new Date();
    
    switch (formData.request_type) {
      case 'seed':
        // Only show ponds with status "active" (ready to start crop)
        filtered = allPonds.filter(pond => pond.status === 'active');
        break;
        
      case 'feed':
      case 'medicine':
        // Only show ponds with status "in_crop" (crop started)
        filtered = allPonds.filter(pond => pond.status === 'in_crop');
        break;
        
      case 'crop_sale':
        // Show ponds that are "in_crop" and near harvest (within 10 days of DOC completion)
        filtered = allPonds.filter(pond => {
          if (pond.status !== 'in_crop') return false;
          
          // Calculate days remaining until DOC completion
          const stockingDate = new Date(pond.stocking_date);
          const docPeriod = pond.doc_requested || 90; // Default 90 days if not specified
          const harvestDate = new Date(stockingDate.getTime() + (docPeriod * 24 * 60 * 60 * 1000));
          const daysRemaining = Math.ceil((harvestDate - currentDate) / (24 * 60 * 60 * 1000));
          
          // Show if within 10 days of harvest or already past harvest date
          return daysRemaining <= 10;
        });
        break;
        
      case 'loan':
        // Only show ponds with status "active" (for new crop investment)
        filtered = allPonds.filter(pond => pond.status === 'active');
        break;
        
      default:
        filtered = allPonds;
    }
    
    setFilteredPonds(filtered);
    setPonds(filtered);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('Loading data...');
      
      // Load requests
      const requestsResponse = await requestsAPI.getAll();
      setRequests(requestsResponse.data);
      
      // Load ponds using a more reliable method
      try {
        const pondsResponse = await pondsAPI.getAllForUser();
        console.log('Ponds from getAllForUser:', pondsResponse);
        setAllPonds(pondsResponse);
        setPonds(pondsResponse);
      } catch (pondsError) {
        console.warn('getAllForUser failed, trying alternative method:', pondsError);
        // Fallback: get farms first, then ponds for each farm
        const farmsResponse = await farmsAPI.getAll();
        const farms = farmsResponse.data;
        console.log('Farms loaded:', farms);
        
        const allPonds = [];
        for (const farm of farms) {
          try {
            const pondResponse = await pondsAPI.getByFarm(farm.id);
            allPonds.push(...pondResponse.data);
          } catch (error) {
            console.warn(`Failed to load ponds for farm ${farm.id}:`, error);
          }
        }
        console.log('All ponds loaded:', allPonds);
        setAllPonds(allPonds);
        setPonds(allPonds);
      }
      
    } catch (err) {
      console.error('Error loading data:', err);
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      pond_id: '',
      request_type: '',
      title: '',
      description: '',
      quantity: '',
      unit: '',
      estimated_cost: '',
      doc_period: '',
      expected_harvest_date: '',
      loan_amount: '',
      loan_type: '',
      repayment_period: ''
    });
    setFieldErrors({});
    setActiveStep(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setViewingRequest(null);
    setOpenDetailsDialog(false);
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.pond_id) errors.pond_id = 'Please select a pond';
    if (!formData.request_type) errors.request_type = 'Please select request type';
    if (!formData.title?.trim()) errors.title = 'Title is required';

    // Type-specific validations
    if (formData.request_type === 'seed' || formData.request_type === 'feed' || formData.request_type === 'medicine') {
      if (!formData.quantity) errors.quantity = 'Quantity is required';
      if (!formData.unit) errors.unit = 'Unit is required';
      if (!formData.estimated_cost) errors.estimated_cost = 'Estimated cost is required';
    }

    if (formData.request_type === 'crop_sale') {
      if (!formData.doc_period) errors.doc_period = 'DOC period is required';
      if (!formData.expected_harvest_date) errors.expected_harvest_date = 'Expected harvest date is required';
    }

    if (formData.request_type === 'loan') {
      if (!formData.loan_amount) errors.loan_amount = 'Loan amount is required';
      if (!formData.loan_type) errors.loan_type = 'Loan type is required';
      if (!formData.repayment_period) errors.repayment_period = 'Repayment period is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setSnackbar({ open: true, message: 'Please fix validation errors', severity: 'error' });
      return;
    }

    try {
      const requestData = {
        pond_id: parseInt(formData.pond_id),
        request_type: formData.request_type,
        title: formData.title,
        description: formData.description || null,
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
        unit: formData.unit || null,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        doc_period: formData.doc_period ? parseInt(formData.doc_period) : null,
        expected_harvest_date: formData.expected_harvest_date || null,
        loan_amount: formData.loan_amount ? parseFloat(formData.loan_amount) : null,
        loan_type: formData.loan_type || null,
        repayment_period: formData.repayment_period ? parseInt(formData.repayment_period) : null
      };

      await requestsAPI.create(requestData);
      setSnackbar({ open: true, message: 'Request created successfully', severity: 'success' });
      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Error creating request:', err);
      setSnackbar({ open: true, message: 'Failed to create request', severity: 'error' });
    }
  };

  const handleViewDetails = (request) => {
    setViewingRequest(request);
    setOpenDetailsDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'fulfilled': return 'info';
      default: return 'default';
    }
  };

  const getRequestTypeInfo = (type) => {
    return requestTypes.find(rt => rt.value === type) || { label: type, icon: <InfoIcon />, color: '#666' };
  };

  const getPondFilterDescription = (requestType) => {
    switch (requestType) {
      case 'seed':
        return "Only ponds with 'Active' status are shown (ready to start new crop)";
      case 'feed':
      case 'medicine':
        return "Only ponds with 'In Crop' status are shown (crop already started)";
      case 'crop_sale':
        return "Only ponds with 'In Crop' status and within 10 days of harvest are shown";
      case 'loan':
        return "Only ponds with 'Active' status are shown (for new crop investment)";
      default:
        return "All available ponds are shown";
    }
  };

  const getPondStatusInfo = (pond) => {
    const currentDate = new Date();
    const stockingDate = new Date(pond.stocking_date);
    const docPeriod = pond.doc_requested || 90;
    const harvestDate = new Date(stockingDate.getTime() + (docPeriod * 24 * 60 * 60 * 1000));
    const daysRemaining = Math.ceil((harvestDate - currentDate) / (24 * 60 * 60 * 1000));
    
    return {
      status: pond.status,
      daysRemaining: daysRemaining,
      harvestDate: harvestDate,
      stockingDate: stockingDate
    };
  };

  const getPondStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'in_crop': return 'primary';
      case 'crop_out': return 'info';
      case 'maintenance': return 'warning';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  const getPondStatusIcon = (status) => {
    switch (status) {
      case 'active': return <AgricultureIcon />;
      case 'in_crop': return <ScheduleIcon />;
      case 'crop_out': return <CheckCircleIcon />;
      case 'maintenance': return <WaterIcon />;
      case 'inactive': return <CancelIcon />;
      default: return <InfoIcon />;
    }
  };

  const getPondStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'in_crop': return 'In Crop';
      case 'crop_out': return 'Crop Out';
      case 'maintenance': return 'Maintenance';
      case 'inactive': return 'Inactive';
      default: return status;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Request Type
            </Typography>
            <Grid container spacing={2}>
              {requestTypes.map((type) => (
                <Grid item xs={12} sm={6} md={4} key={type.value}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: formData.request_type === type.value ? 2 : 1,
                      borderColor: formData.request_type === type.value ? type.color : 'divider',
                      '&:hover': { borderColor: type.color, borderWidth: 2 }
                    }}
                    onClick={() => setFormData({ ...formData, request_type: type.value })}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Box sx={{ color: type.color, mb: 1 }}>
                        {type.icon}
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {type.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {fieldErrors.request_type && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {fieldErrors.request_type}
              </Typography>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Pond ({loading ? 'Loading...' : `${filteredPonds.length} ponds available`})
            </Typography>
            {formData.request_type && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {getPondFilterDescription(formData.request_type)}
              </Typography>
            )}
            <FormControl fullWidth error={!!fieldErrors.pond_id}>
              <InputLabel>Select Pond *</InputLabel>
              <Select
                value={formData.pond_id}
                onChange={(e) => {
                  const selectedPondId = e.target.value;
                  setFormData({ ...formData, pond_id: selectedPondId });
                  
                  // Auto-fill request details based on selected pond for seed requests
                  if (formData.request_type === 'seed' && selectedPondId) {
                    const selectedPond = allPonds.find(p => p.id === parseInt(selectedPondId));
                    if (selectedPond) {
                      setFormData(prev => ({
                        ...prev,
                        pond_id: selectedPondId,
                        title: `${selectedPond.species} Seed Request - ${selectedPond.pond_name}`,
                        quantity: Math.ceil(selectedPond.pond_area * (selectedPond.stocking_density || 50)), // Calculate based on area and density
                        unit: 'pieces',
                        estimated_cost: Math.ceil(selectedPond.pond_area * (selectedPond.stocking_density || 50) * 0.5) // Rough estimate: 0.5 per piece
                      }));
                    }
                  }
                }}
                label="Select Pond *"
                disabled={loading}
              >
                {filteredPonds.length === 0 ? (
                  <MenuItem disabled>
                    <Typography color="text.secondary">
                      {allPonds.length === 0 ? 'No ponds available' : 'No ponds match the criteria for this request type'}
                    </Typography>
                  </MenuItem>
                ) : (
                  filteredPonds.map((pond) => {
                    const statusInfo = getPondStatusInfo(pond);
                    return (
                      <MenuItem key={pond.id} value={pond.id}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {pond.pond_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {pond.species} • {pond.pond_area ? parseFloat(pond.pond_area).toFixed(2) : 'N/A'} hectares
                            </Typography>
                            <Chip
                              label={getPondStatusLabel(pond.status)}
                              color={getPondStatusColor(pond.status)}
                              size="small"
                              icon={getPondStatusIcon(pond.status)}
                            />
                          </Box>
                          {pond.status === 'in_crop' && (
                            <Typography variant="caption" color="primary" display="block">
                              DOC: {statusInfo.daysRemaining > 0 ? `${statusInfo.daysRemaining} days remaining` : 'Ready for harvest'}
                            </Typography>
                          )}
                          {formData.request_type === 'seed' && pond.stocking_density && (
                            <Typography variant="caption" color="info.main" display="block">
                              Stocking Density: {pond.stocking_density ? parseFloat(pond.stocking_density).toFixed(2) : 'N/A'} PL/m²
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })
                )}
              </Select>
              {fieldErrors.pond_id && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 2 }}>
                  {fieldErrors.pond_id}
                </Typography>
              )}
            </FormControl>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Request Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Request Title *"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  error={!!fieldErrors.title}
                  helperText={fieldErrors.title}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>

              {/* Input-specific fields */}
              {(formData.request_type === 'seed' || formData.request_type === 'feed' || formData.request_type === 'medicine') && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Quantity *"
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      error={!!fieldErrors.quantity}
                      helperText={fieldErrors.quantity}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth error={!!fieldErrors.unit}>
                      <InputLabel>Unit *</InputLabel>
                      <Select
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        label="Unit *"
                      >
                        {units.map((unit) => (
                          <MenuItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldErrors.unit && (
                        <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 2 }}>
                          {fieldErrors.unit}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Estimated Cost (₹) *"
                      type="number"
                      value={formData.estimated_cost}
                      onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                      error={!!fieldErrors.estimated_cost}
                      helperText={fieldErrors.estimated_cost}
                    />
                  </Grid>
                </>
              )}

              {/* Crop sale specific fields */}
              {formData.request_type === 'crop_sale' && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="DOC Period (Days) *"
                      type="number"
                      value={formData.doc_period}
                      onChange={(e) => setFormData({ ...formData, doc_period: e.target.value })}
                      error={!!fieldErrors.doc_period}
                      helperText={fieldErrors.doc_period}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Expected Harvest Date *"
                      type="date"
                      value={formData.expected_harvest_date}
                      onChange={(e) => setFormData({ ...formData, expected_harvest_date: e.target.value })}
                      error={!!fieldErrors.expected_harvest_date}
                      helperText={fieldErrors.expected_harvest_date}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}

              {/* Loan specific fields */}
              {formData.request_type === 'loan' && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Loan Amount (₹) *"
                      type="number"
                      value={formData.loan_amount}
                      onChange={(e) => setFormData({ ...formData, loan_amount: e.target.value })}
                      error={!!fieldErrors.loan_amount}
                      helperText={fieldErrors.loan_amount}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth error={!!fieldErrors.loan_type}>
                      <InputLabel>Loan Type *</InputLabel>
                      <Select
                        value={formData.loan_type}
                        onChange={(e) => setFormData({ ...formData, loan_type: e.target.value })}
                        label="Loan Type *"
                      >
                        {loanTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldErrors.loan_type && (
                        <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 2 }}>
                          {fieldErrors.loan_type}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Repayment Period (Months) *"
                      type="number"
                      value={formData.repayment_period}
                      onChange={(e) => setFormData({ ...formData, repayment_period: e.target.value })}
                      error={!!fieldErrors.repayment_period}
                      helperText={fieldErrors.repayment_period}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Submit
            </Typography>
            <Card sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Request Summary
              </Typography>
              <Typography><strong>Type:</strong> {getRequestTypeInfo(formData.request_type).label}</Typography>
              <Typography><strong>Pond:</strong> {ponds.find(p => p.id === parseInt(formData.pond_id))?.pond_name}</Typography>
              <Typography><strong>Title:</strong> {formData.title}</Typography>
              {formData.description && <Typography><strong>Description:</strong> {formData.description}</Typography>}
              {formData.quantity && <Typography><strong>Quantity:</strong> {formData.quantity} {formData.unit}</Typography>}
              {formData.estimated_cost && <Typography><strong>Estimated Cost:</strong> ₹{formData.estimated_cost}</Typography>}
              {formData.doc_period && <Typography><strong>DOC Period:</strong> {formData.doc_period} days</Typography>}
              {formData.expected_harvest_date && <Typography><strong>Expected Harvest:</strong> {formData.expected_harvest_date}</Typography>}
              {formData.loan_amount && <Typography><strong>Loan Amount:</strong> ₹{parseFloat(formData.loan_amount).toFixed(2)}</Typography>}
              {formData.loan_type && <Typography><strong>Loan Type:</strong> {loanTypes.find(lt => lt.value === formData.loan_type)?.label}</Typography>}
              {formData.repayment_period && <Typography><strong>Repayment Period:</strong> {formData.repayment_period} months</Typography>}
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading requests...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{ borderRadius: 2 }}
        >
          New Request
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Request #</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Pond</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(filterTypes ? requests.filter((r) => filterTypes.includes(r.request_type)) : requests).map((request) => {
                  const typeInfo = getRequestTypeInfo(request.request_type);
                  return (
                    <TableRow key={request.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {request.request_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ color: typeInfo.color }}>
                            {typeInfo.icon}
                          </Box>
                          <Typography variant="body2">
                            {typeInfo.label}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{request.title}</TableCell>
                      <TableCell>{request.pond_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={request.status}
                          color={getStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(request.requested_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            color="primary"
                            onClick={() => handleViewDetails(request)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {requests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No requests found. Create your first request to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Request Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create New Request</Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStepContent()}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit}>
              Submit Request
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Request Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Request Details</Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingRequest && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Basic Information
                    </Typography>
                    <Typography><strong>Request #:</strong> {viewingRequest.request_number}</Typography>
                    <Typography><strong>Type:</strong> {getRequestTypeInfo(viewingRequest.request_type).label}</Typography>
                    <Typography><strong>Title:</strong> {viewingRequest.title}</Typography>
                    <Typography><strong>Status:</strong> 
                      <Chip
                        label={viewingRequest.status}
                        color={getStatusColor(viewingRequest.status)}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography><strong>Requested Date:</strong> {new Date(viewingRequest.requested_date).toLocaleDateString()}</Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Pond Information
                    </Typography>
                    <Typography><strong>Pond:</strong> {viewingRequest.pond_name}</Typography>
                    <Typography><strong>Requested By:</strong> {viewingRequest.user_name}</Typography>
                  </Card>
                </Grid>

                {viewingRequest.description && (
                  <Grid item xs={12}>
                    <Card sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Description
                      </Typography>
                      <Typography>{viewingRequest.description}</Typography>
                    </Card>
                  </Grid>
                )}

                {(viewingRequest.quantity || viewingRequest.estimated_cost) && (
                  <Grid item xs={12}>
                    <Card sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Request Details
                      </Typography>
                      {viewingRequest.quantity && (
                        <Typography><strong>Quantity:</strong> {viewingRequest.quantity} {viewingRequest.unit}</Typography>
                      )}
                      {viewingRequest.estimated_cost && (
                        <Typography><strong>Estimated Cost:</strong> ₹{viewingRequest.estimated_cost}</Typography>
                      )}
                      {viewingRequest.doc_period && (
                        <Typography><strong>DOC Period:</strong> {viewingRequest.doc_period} days</Typography>
                      )}
                      {viewingRequest.expected_harvest_date && (
                        <Typography><strong>Expected Harvest:</strong> {new Date(viewingRequest.expected_harvest_date).toLocaleDateString()}</Typography>
                      )}
                      {viewingRequest.loan_amount && (
                        <Typography><strong>Loan Amount:</strong> ₹{viewingRequest.loan_amount ? parseFloat(viewingRequest.loan_amount).toFixed(2) : '0.00'}</Typography>
                      )}
                      {viewingRequest.loan_type && (
                        <Typography><strong>Loan Type:</strong> {loanTypes.find(lt => lt.value === viewingRequest.loan_type)?.label}</Typography>
                      )}
                      {viewingRequest.repayment_period && (
                        <Typography><strong>Repayment Period:</strong> {viewingRequest.repayment_period} months</Typography>
                      )}
                    </Card>
                  </Grid>
                )}

                {viewingRequest.status === 'rejected' && viewingRequest.rejection_reason && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography><strong>Rejection Reason:</strong> {viewingRequest.rejection_reason}</Typography>
                    </Alert>
                  </Grid>
                )}

                {viewingRequest.status === 'approved' && viewingRequest.approver_name && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      <Typography><strong>Approved by:</strong> {viewingRequest.approver_name}</Typography>
                      <Typography><strong>Approved on:</strong> {new Date(viewingRequest.approved_at).toLocaleDateString()}</Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestManagement;
